import type { ColumnsType } from 'antd/es/table';
import { Alert, Drawer, Pagination, Spin, Table, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import type {
  IWorkJourneyManagementDayReportJourneyItem,
  IWorkJourneyManagementDayReportSummary,
  IWorkJourneyManagementDayReportUserItem,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { getFullUrl } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { DateUtil } from '@src/packages/utils/dates';
import { getRouteToWorkJourneyMonthMatrix } from '@src/packages/workJourney/constants';

import type { DayReportJourneyDetailState } from '../../hooks/useWorkJourneyCalendarDayReportDrawer';
import { WorkJourneyDayReportStatusBadge } from './components/WorkJourneyDayReportStatusBadge';
import styles from './styles.module.less';

type WorkJourneyCalendarDayReportDrawerProps = {
  open: boolean;
  date: string | null;
  journeys: IWorkJourneyManagementDayReportJourneyItem[];
  summary: IWorkJourneyManagementDayReportSummary;
  totalJourneys: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  expandedJourneyId: string | null;
  detailByJourneyId: Record<string, DayReportJourneyDetailState>;
  onClose: () => void;
  onPageChange: (page: number) => void;
  onToggleJourney: (workJourneyConfigId: string) => void;
};

const SummaryStrip = ({
  summary,
}: {
  summary: IWorkJourneyManagementDayReportSummary;
}) => {
  const intl = useIntl();
  const items = [
    {
      key: 'people',
      labelId: 'workJourney.management.calendar.dayReport.summary.people',
      value: summary.people,
    },
    {
      key: 'journeys',
      labelId: 'workJourney.management.calendar.dayReport.summary.journeys',
      value: summary.journeys,
    },
    {
      key: 'present',
      labelId: 'workJourney.management.calendar.dayReport.summary.present',
      value: summary.present,
    },
    {
      key: 'justified',
      labelId: 'workJourney.management.calendar.dayReport.summary.justifiedAbsence',
      value: summary.justifiedAbsence,
    },
    {
      key: 'unjustified',
      labelId: 'workJourney.management.calendar.dayReport.summary.unjustifiedAbsence',
      value: summary.unjustifiedAbsence,
    },
  ];

  return (
    <div className={styles.summaryStrip} data-testid="day-report-summary">
      {items.map(item => (
        <div key={item.key} className={styles.summaryItem}>
          <span className={styles.summaryValue}>{item.value}</span>
          <span className={styles.summaryLabel}>
            {intl.formatMessage({ id: item.labelId })}
          </span>
        </div>
      ))}
    </div>
  );
};

export const WorkJourneyCalendarDayReportDrawer = ({
  open,
  date,
  journeys,
  summary,
  totalJourneys,
  page,
  pageSize,
  loading,
  error,
  expandedJourneyId,
  detailByJourneyId,
  onClose,
  onPageChange,
  onToggleJourney,
}: WorkJourneyCalendarDayReportDrawerProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();

  const title = useMemo(() => {
    if (!date) {
      return intl.formatMessage({
        id: 'workJourney.management.calendar.dayReport.titleFallback',
      });
    }
    const formatted = DateUtil.toDateString(date, DateUtil.DEFAULT_DT_DATE_FORMAT) || date;
    return intl.formatMessage(
      { id: 'workJourney.management.calendar.dayReport.title' },
      { date: formatted },
    );
  }, [date, intl]);

  const handleOpenMatrix = (workJourneyConfigId: string) => {
    if (!date) return;
    const parsed = DateUtil.toDateOrNull(date) ?? new Date(`${date}T12:00:00`);
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;
    history.push(
      getFullUrl(
        getRouteToWorkJourneyMonthMatrix({
          workJourneyConfigId,
          year,
          month,
        }),
        url,
      ),
    );
  };

  const userColumns: ColumnsType<IWorkJourneyManagementDayReportUserItem> = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.user' }),
      key: 'user',
      render: (record: IWorkJourneyManagementDayReportUserItem) => (
        <div className={styles.userCell}>
          <MemberAvatar
            size={32}
            user={{ id: record.userId, name: record.userDisplayName }}
          />
          <Tooltip title={record.userDisplayName}>
            <span className={styles.userName}>{record.userDisplayName}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.status' }),
      key: 'status',
      render: (record: IWorkJourneyManagementDayReportUserItem) => (
        <WorkJourneyDayReportStatusBadge item={record} />
      ),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 140,
      render: (record: IWorkJourneyManagementDayReportUserItem) => (
        <ActionButton.Container
          maxVisibleButtons={1}
          buttons={[
            <ActionButton
              key={`${record.id}-see-more`}
              label={intl.formatMessage({
                id: 'workJourney.management.calendar.dayDrawer.seeMore',
              })}
              onClick={() => handleOpenMatrix(record.workJourneyConfigId)}
              type="primary"
              color="primary"
              size="medium"
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.GET}
              className={styles.actionButton}
              disabledScaling
            />,
          ]}
        />
      ),
    },
  ];

  const renderExpandedRow = (journey: IWorkJourneyManagementDayReportJourneyItem) => {
    const detail = detailByJourneyId[journey.workJourneyConfigId];
    if (!detail || detail.loading) {
      return (
        <div className={styles.detailLoading}>
          <Spin size="small" />
        </div>
      );
    }
    if (detail.error) {
      return <Alert type="error" showIcon message={detail.error} />;
    }
    if (detail.items.length === 0) {
      return (
        <p className={styles.emptyState}>
          {intl.formatMessage({
            id: 'workJourney.management.calendar.dayReport.detailEmpty',
          })}
        </p>
      );
    }
    return (
      <Table
        className={styles.detailTable}
        rowKey="id"
        size="small"
        pagination={false}
        columns={userColumns}
        dataSource={detail.items}
      />
    );
  };

  const journeyColumns: ColumnsType<IWorkJourneyManagementDayReportJourneyItem> = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.columns.journey' }),
      key: 'journey',
      render: (record: IWorkJourneyManagementDayReportJourneyItem) => (
        <Tooltip title={record.workJourneyConfigName}>
          <span className={styles.journeyCell}>{record.workJourneyConfigName}</span>
        </Tooltip>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'workJourney.management.calendar.dayReport.columns.people',
      }),
      key: 'people',
      width: 100,
      align: 'center',
      render: (record: IWorkJourneyManagementDayReportJourneyItem) => record.peopleCount,
    },
    {
      title: intl.formatMessage({
        id: 'workJourney.management.calendar.dayReport.summary.present',
      }),
      key: 'present',
      width: 100,
      align: 'center',
      render: (record: IWorkJourneyManagementDayReportJourneyItem) => record.presentCount,
    },
    {
      title: intl.formatMessage({
        id: 'workJourney.management.calendar.dayReport.summary.justifiedAbsence',
      }),
      key: 'justified',
      width: 120,
      align: 'center',
      render: (record: IWorkJourneyManagementDayReportJourneyItem) =>
        record.justifiedAbsenceCount,
    },
    {
      title: intl.formatMessage({
        id: 'workJourney.management.calendar.dayReport.summary.unjustifiedAbsence',
      }),
      key: 'unjustified',
      width: 120,
      align: 'center',
      render: (record: IWorkJourneyManagementDayReportJourneyItem) =>
        record.unjustifiedAbsenceCount,
    },
  ];

  return (
    <Drawer
      className={styles.drawer}
      open={open}
      onClose={onClose}
      width={920}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer title={<Title.Header value={title} />} goBack={onClose} />
        </BoxContainer>

        <BoxContainer content>
          <SummaryStrip summary={summary} />

          <h3 className={styles.sectionTitle}>
            {intl.formatMessage({
              id: 'workJourney.management.calendar.dayReport.journeysTitle',
            })}
          </h3>

          {error ? (
            <Alert type="error" showIcon message={error} className={styles.errorAlert} />
          ) : journeys.length === 0 && !loading ? (
            <p className={styles.emptyState}>
              {intl.formatMessage({
                id: 'workJourney.management.calendar.dayReport.empty',
              })}
            </p>
          ) : (
            <>
              <Table
                rowKey="workJourneyConfigId"
                loading={loading}
                columns={journeyColumns}
                dataSource={journeys}
                pagination={false}
                expandable={{
                  expandedRowKeys: expandedJourneyId ? [expandedJourneyId] : [],
                  onExpand: (expanded, record) => {
                    if (expanded || expandedJourneyId === record.workJourneyConfigId) {
                      onToggleJourney(record.workJourneyConfigId);
                    }
                  },
                  expandedRowRender: renderExpandedRow,
                }}
              />
              {totalJourneys > pageSize ? (
                <div className={styles.pagination}>
                  <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={totalJourneys}
                    onChange={onPageChange}
                    showSizeChanger={false}
                  />
                </div>
              ) : null}
            </>
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
